import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  Check,
  CheckCheck,
  Loader2,
  Pencil,
  Plus,
  Store,
  Tags,
  Trash2,
  X,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useConfirm } from "@/components/Confirm";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import Layout from "@/components/Layout";
import SelectionBar from "@/components/SelectionBar";
import { EmptyState, LoadingPanel } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Categories() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const { settings, refresh } = useSettings();
  // The example code has to use this business's prefix, not a fixed one.
  const root = (settings.code_prefix || "INV").toUpperCase();
  // The platform owner is an admin of every client workspace they open.
  const isAdmin = ["owner", "admin"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ prefix: "", name: "", department_id: "" });
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ prefix: "", name: "", department_id: "" });
  const [selected, setSelected] = useState([]);
  const [merging, setMerging] = useState(false);
  const [mergeInto, setMergeInto] = useState("");
  const [renumber, setRenumber] = useState(true);

  // Departments live in Settings → Shop — this page only assigns a category
  // to one, same as it always just picked a prefix.
  const [departments, setDepartments] = useState([]);
  const deptById = useMemo(
    () => Object.fromEntries(departments.map((d) => [d.department_id, d])),
    [departments]
  );

  const loadDepartments = useCallback(async () => {
    try {
      const { data } = await api.get("/departments");
      setDepartments(data);
    } catch {
      // Non-critical — the department column just shows "none" if this fails.
    }
  }, []);

  useEffect(() => {
    if (settings.shop?.enabled) loadDepartments();
  }, [settings.shop?.enabled, loadDepartments]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/categories");
      setRows(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!form.prefix.trim() || !form.name.trim()) {
      toast.error("Enter both a prefix and a name");
      return;
    }
    try {
      await api.post("/categories", { ...form, department_id: form.department_id || null });
      toast.success("Category added");
      setAdding(false);
      setForm({ prefix: "", name: "", department_id: "" });
      load();
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const startEdit = (c) => {
    setEditingId(c.category_id);
    setDraft({ prefix: c.prefix, name: c.name, department_id: c.department_id || "" });
  };

  const saveEdit = async (c) => {
    try {
      await api.put(`/categories/${c.category_id}`, {
        ...draft,
        department_id: draft.department_id || null,
      });
      toast.success("Category updated");
      setEditingId(null);
      load();
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  // Two categories sharing a name both claim the same items, so neither can be
  // deleted. Merging is the way out.
  const duplicateNames = useMemo(() => {
    const seen = {};
    rows.forEach((c) => {
      const key = c.name.trim().toLowerCase();
      seen[key] = (seen[key] || 0) + 1;
    });
    return new Set(Object.keys(seen).filter((k) => seen[k] > 1));
  }, [rows]);

  const chosen = rows.filter((c) => selected.includes(c.category_id));
  const affected = chosen
    .filter((c) => c.category_id !== mergeInto)
    .reduce((n, c) => n + (c.item_count || 0), 0);

  const toggle = (id) =>
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const removeSelected = async () => {
    const names = chosen.map((c) => c.name).join(", ");
    const ok = await confirm({
      title: `Delete ${chosen.length} categor${chosen.length === 1 ? "y" : "ies"}?`,
      body: `${names}. Any still holding items are kept — move or delete those items first.`,
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.post("/categories/delete", { category_ids: selected });
      if (data.removed.length) {
        toast.success(`Deleted ${data.removed.length}`);
      }
      if (data.kept.length) {
        // Say which survived and why, rather than silently doing less than asked.
        toast.error(
          `Kept ${data.kept.map((k) => `${k.name} (${k.items} items)`).join(", ")}`
        );
      }
      setSelected([]);
      load();
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const doMerge = async () => {
    if (!mergeInto) return;
    try {
      const { data } = await api.post("/categories/merge", {
        from_ids: selected.filter((id) => id !== mergeInto),
        into_id: mergeInto,
        renumber,
      });
      toast.success(
        data.recoded
          ? `${data.moved} item${data.moved === 1 ? "" : "s"} moved to ${data.into}, ${data.recoded} renumbered`
          : `${data.moved} item${data.moved === 1 ? "" : "s"} moved to ${data.into}`
      );
      setMerging(false);
      setSelected([]);
      setMergeInto("");
      load();
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  // The bulk "Add to department" control living inside the selection bar —
  // "" means nothing chosen yet, "__none__" means unassign.
  const [bulkDept, setBulkDept] = useState("");
  const [applyingDept, setApplyingDept] = useState(false);

  const applyBulkDept = async () => {
    if (!bulkDept) return;
    setApplyingDept(true);
    try {
      const { data } = await api.post("/categories/department", {
        category_ids: selected,
        department_id: bulkDept === "__none__" ? null : bulkDept,
      });
      const label = bulkDept === "__none__" ? null : deptById[bulkDept]?.name;
      toast.success(
        label
          ? `${data.updated.length} categor${data.updated.length === 1 ? "y" : "ies"} added to ${label}`
          : `${data.updated.length} categor${data.updated.length === 1 ? "y" : "ies"} unassigned`
      );
      setSelected([]);
      setBulkDept("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setApplyingDept(false);
    }
  };

  const remove = async (c) => {
    const ok = await confirm({
      title: `Delete the ${c.name} category?`,
      body: "Only possible while no items are using it.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/categories/${c.category_id}`);
      toast.success("Category deleted");
      load();
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Layout
      title="Categories"
      subtitle="The prefix decides how new item codes are numbered"
      action={
        <>
          {isAdmin && rows.length > 1 && (
            <Button
              variant="outline"
              onClick={() =>
                setSelected((cur) =>
                  cur.length === rows.length ? [] : rows.map((c) => c.category_id)
                )
              }
            >
              <CheckCheck className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">
                {selected.length === rows.length ? "Select none" : "Select all"}
              </span>
            </Button>
          )}
          {isAdmin && (
            <Button onClick={() => setAdding(true)} className="bg-brand hover:bg-brand-hover">
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Add category</span>
            </Button>
          )}
        </>
      }
    >
      <SelectionBar
        top="top-[68px]"
        secondaryLabel="Delete"
        secondaryIcon={Trash2}
        onSecondary={removeSelected}
        count={selected.length}
        detail={`${chosen.reduce((n, c) => n + (c.item_count || 0), 0)} items between them`}
        onClear={() => setSelected([])}
        onAction={() => {
          setMergeInto(selected[0]);
          setMerging(true);
        }}
        middle={
          settings.shop?.enabled &&
          departments.length > 0 && (
            <div className="flex items-center gap-1 bg-white border border-line rounded-md pl-2 h-8 shrink-0">
              <Store className="w-3.5 h-3.5 text-muted2 shrink-0" />
              <Select value={bulkDept} onValueChange={setBulkDept}>
                <SelectTrigger className="h-8 border-0 shadow-none px-1.5 text-[12.5px] w-auto gap-1 focus:ring-0">
                  <SelectValue placeholder="Add to department…" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((d) => (
                    <SelectItem key={d.department_id} value={d.department_id}>
                      {d.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="__none__">No department (unassign)</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                disabled={!bulkDept || applyingDept}
                onClick={applyBulkDept}
                className="h-6 px-2.5 text-[12px] bg-brand hover:bg-brand-hover my-auto mr-0.5"
              >
                {applyingDept ? <Loader2 className="w-3 h-3 animate-spin" /> : "Apply"}
              </Button>
            </div>
          )
        }
      />

      {loading ? (
        <LoadingPanel rows={6} />
      ) : rows.length === 0 ? (
        <div className="panel">
          <EmptyState
            icon={Tags}
            title="No categories yet"
            body="Add one to start coding items, for example TL for Tools."
            action={
              isAdmin && (
                <Button onClick={() => setAdding(true)} className="bg-brand hover:bg-brand-hover">
                  <Plus className="w-4 h-4 mr-2" /> Add category
                </Button>
              )
            }
          />
        </div>
      ) : (
        <>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((c) => {
            const editing = editingId === c.category_id;
            return (
              <div
                key={c.category_id}
                className={cn(
                  "panel panel-pad group transition-colors",
                  selected.includes(c.category_id) && "ring-1 ring-brand bg-brand-light/30"
                )}
              >
                {editing ? (
                  <div className="space-y-3">
                    <Input
                      value={draft.prefix}
                      onChange={(e) =>
                        setDraft({ ...draft, prefix: e.target.value.toUpperCase() })
                      }
                      maxLength={4}
                      className="font-mono w-24"
                    />
                    <Input
                      value={draft.name}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    />

                    {settings.shop?.enabled && (
                      <div className="space-y-1">
                        <Label className="text-[11.5px]">Department</Label>
                        <Select
                          value={draft.department_id || "none"}
                          onValueChange={(v) =>
                            setDraft({ ...draft, department_id: v === "none" ? "" : v })
                          }
                        >
                          <SelectTrigger className="h-9 text-[13px]">
                            <SelectValue placeholder="— none —" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— none —</SelectItem>
                            {departments.map((d) => (
                              <SelectItem key={d.department_id} value={d.department_id}>
                                {d.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[11px] text-muted2">
                          Groups this category into a doorway on the shop.{" "}
                          <Link to="/settings" className="link-quiet">
                            Manage departments
                          </Link>
                        </p>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => saveEdit(c)}
                        className="bg-brand hover:bg-brand-hover"
                      >
                        <Check className="w-3.5 h-3.5 mr-1.5" /> Save
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      {isAdmin && rows.length > 1 && (
                        <button
                          onClick={() => toggle(c.category_id)}
                          aria-label={`Select ${c.name}`}
                          className={cn(
                            "w-[18px] h-[18px] rounded-full border shrink-0 mt-1 flex items-center justify-center transition-colors",
                            selected.includes(c.category_id)
                              ? "bg-brand border-brand"
                              : "border-line hover:border-brand-ring"
                          )}
                        >
                          {selected.includes(c.category_id) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </button>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start gap-3">
                          <div className="inline-flex items-center justify-center px-2.5 py-1 rounded-lg bg-gold-light shrink-0">
                            <span className="font-mono text-[13px] font-semibold text-gold">
                              {c.prefix}
                            </span>
                          </div>
                        </div>
                        <div className="font-display text-[16px] font-semibold text-ink mt-2.5 leading-snug">
                          {c.name}
                        </div>
                        <div className="text-[12.5px] text-muted2 mt-1">
                          {c.item_count} item{c.item_count === 1 ? "" : "s"}
                        </div>
                        {settings.shop?.enabled && (
                          <div
                            className={cn(
                              "text-[11px] mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5",
                              c.department_id && deptById[c.department_id]
                                ? "bg-stock-in/10 text-stock-in"
                                : "bg-surface text-muted2 border border-line"
                            )}
                          >
                            <Store className="w-3 h-3" />
                            {c.department_id && deptById[c.department_id]
                              ? deptById[c.department_id].name
                              : "No department — won't show on the storefront"}
                          </div>
                        )}
                        {duplicateNames.has(c.name.trim().toLowerCase()) && (
                          <div className="mt-2 inline-flex items-start gap-1.5 text-[11.5px] text-gold bg-gold-light rounded-lg px-2 py-1 leading-snug">
                            <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                            <span>
                              Another category has this name, so they share these
                              items. Merge them.
                            </span>
                          </div>
                        )}
                      </div>
                      {isAdmin && (
                        <div className="flex gap-0.5 shrink-0">
                          <button
                            onClick={() => startEdit(c)}
                            className="p-1.5 rounded-lg text-muted2 hover:text-brand hover:bg-brand-light"
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => remove(c)}
                            className="p-1.5 rounded-lg text-muted2 hover:text-stock-out hover:bg-rose-50"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="mt-4 pt-3 border-t border-line/70 font-mono text-[11.5px] text-muted2">
                      {root}-{c.prefix}-001
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        </>
      )}

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Add a category</DialogTitle>
            <DialogDescription>
              The prefix appears in the middle of every item code in this category.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="cp">Prefix</Label>
              <Input
                id="cp"
                value={form.prefix}
                onChange={(e) => setForm({ ...form, prefix: e.target.value.toUpperCase() })}
                maxLength={4}
                placeholder="TL"
                className="font-mono w-28"
              />
              <p className="text-[12px] text-muted2">Two to four letters.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cn">Name</Label>
              <Input
                id="cn"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Tools"
              />
            </div>
            {settings.shop?.enabled && (
              <div className="space-y-1.5">
                <Label htmlFor="cdept">Department (optional)</Label>
                {departments.length > 0 ? (
                  <Select
                    value={form.department_id || "none"}
                    onValueChange={(v) =>
                      setForm({ ...form, department_id: v === "none" ? "" : v })
                    }
                  >
                    <SelectTrigger id="cdept">
                      <SelectValue placeholder="— none —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— none —</SelectItem>
                      {departments.map((d) => (
                        <SelectItem key={d.department_id} value={d.department_id}>
                          {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-[12px] text-muted2">
                    No departments yet —{" "}
                    <Link to="/settings" className="link-quiet">
                      create one in Settings
                    </Link>{" "}
                    to group this into a storefront doorway. It can be assigned later either way.
                  </p>
                )}
              </div>
            )}
            {form.prefix && (
              <div className="rounded-lg bg-brand-light px-3.5 py-2.5 text-[13px] text-brand flex items-center justify-between">
                <span>First code will be</span>
                <span className="font-mono font-semibold">
                  {root}-{form.prefix}-001
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button onClick={create} className="bg-brand hover:bg-brand-hover">
              Add category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={merging} onOpenChange={(v) => !v && setMerging(false)}>
        <DialogContent className="sm:max-w-[440px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Merge {chosen.length} categories
            </DialogTitle>
            <DialogDescription>
              Their items move into the one you keep, and the emptied categories are removed. Nothing is deleted.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            <div className="space-y-2">
              <Label>Keep</Label>
              <div className="space-y-1.5">
                {chosen.map((c) => (
                  <button
                    key={c.category_id}
                    onClick={() => setMergeInto(c.category_id)}
                    className={cn(
                      "w-full text-left rounded-xl border px-3.5 py-2.5 transition-colors flex items-center gap-3",
                      mergeInto === c.category_id
                        ? "border-brand bg-brand-light"
                        : "border-line hover:bg-brand-light/40"
                    )}
                  >
                    <span
                      className={cn(
                        "w-[15px] h-[15px] rounded-full border shrink-0",
                        mergeInto === c.category_id ? "bg-brand border-brand" : "border-line"
                      )}
                    />
                    <span className="font-mono text-[12px] font-semibold text-gold bg-gold-light rounded px-1.5 py-0.5 shrink-0">
                      {c.prefix}
                    </span>
                    <span className="text-[13.5px] text-ink flex-1 truncate">{c.name}</span>
                    <span className="text-[12px] text-muted2">{c.item_count}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Item codes</Label>
              {[
                {
                  on: true,
                  title: "Renumber to the kept prefix",
                  body: `Codes change to ${root}-${
                    chosen.find((c) => c.category_id === mergeInto)?.prefix || "—"
                  }-… everywhere they appear, including past purchases and sales.`,
                },
                {
                  on: false,
                  title: "Leave them as they are",
                  body: "Codes keep their old prefix. Anything printed or exported still matches.",
                },
              ].map((opt) => (
                <button
                  key={opt.title}
                  onClick={() => setRenumber(opt.on)}
                  className={cn(
                    "w-full text-left rounded-xl border px-3.5 py-2.5 transition-colors",
                    renumber === opt.on
                      ? "border-brand bg-brand-light"
                      : "border-line hover:bg-brand-light/40"
                  )}
                >
                  <div
                    className={cn(
                      "text-[13px] font-medium",
                      renumber === opt.on ? "text-brand" : "text-ink"
                    )}
                  >
                    {opt.title}
                  </div>
                  <div className="text-[12px] text-muted2 mt-0.5 leading-relaxed">{opt.body}</div>
                </button>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setMerging(false)}>
              Cancel
            </Button>
            <Button
              onClick={doMerge}
              disabled={!mergeInto}
              className="bg-brand hover:bg-brand-hover"
            >
              Merge {affected} item{affected === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </Layout>
  );
}
